// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { post } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { OmsAddress, OmsContact } from '../types/oms';

/**
 * Qcells-internal provisioning. Every route here must be gated server-side by
 * `require_platform_admin` (a customer token gets a 403). That BFF dependency
 * ships with the routes themselves and DOES NOT EXIST YET — until it does, the
 * UI's own gate is a convenience, not a security boundary, and this build must
 * not be deployed anywhere a customer can sign in.
 *
 * Creates start a Temporal workflow rather than writing OMS directly, because
 * provisioning is more than the record: the org run waits for IAM to reconcile
 * the tenant, and the site run also provisions the `dc-{site_id}` repository.
 */

/** Body of `OrganizationCreation_v0` — OMS's `OrganizationCreate` fields. */
export interface ProvisionOrganizationRequest {
  organizationKey: string;
  preferredName: string;
  legalEntityName: string;
  timeZone: string;
  primaryBusinessSegment: string;
  address: OmsAddress;
  url: string;
  contacts: OmsContact[];
}

/** Body of `SiteCreation_v0`. `siteId` is server-assigned, never sent. */
export interface ProvisionSiteRequest {
  organizationId: string;
  organizationName: string;
  organizationDescription: string;
  siteKey: string;
  regionId: string;
  label: string;
  siteName: string;
  siteDescription: string;
}

/** A started run. `runId` is the Temporal workflow id. */
export interface ProvisioningRun {
  runId: string;
  status: string;
  organizationId?: string;
  siteId?: string;
}

export const provisionOrganization = async (
  data: ProvisionOrganizationRequest
): Promise<ProvisioningRun> => post<ProvisioningRun>(ENDPOINTS.PROVISIONING_ORGANIZATIONS, data);

export const provisionSite = async (data: ProvisionSiteRequest): Promise<ProvisioningRun> =>
  post<ProvisioningRun>(ENDPOINTS.PROVISIONING_SITES, data);
